import { z } from 'zod';

import {
  AcceptedEdgeSchema,
  EdgeDirectionSchema,
  EdgePublicationStatusSchema,
  RelationIdSchema,
} from './edge.js';
import { PersonSchema } from './person.js';
import {
  CoreLensIdSchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  NonEmptyStringSchema,
  QidSchema,
  Sha256Schema,
} from './primitives.js';
import { SourceSchema } from './source.js';
import { QualifiedTimeSchema, relationshipTimeOrderIsValid } from './time.js';

export const Point3Schema = z.tuple([
  z.number().finite(),
  z.number().finite(),
  z.number().finite(),
]);

export type Point3 = z.infer<typeof Point3Schema>;

export const GraphLayoutSchema = z.object({
  id: NonEmptyStringSchema,
  version: z.number().int().positive(),
  algorithm: NonEmptyStringSchema,
  dimensions: z.literal(3),
  coordinates: z.record(QidSchema, Point3Schema),
});

export type GraphLayout = z.infer<typeof GraphLayoutSchema>;

export const FeaturedTraceSchema = z.object({
  id: NonEmptyStringSchema,
  source: QidSchema,
  target: QidSchema,
  edgeIds: z.array(NonEmptyStringSchema).min(1),
});

export type FeaturedTrace = z.infer<typeof FeaturedTraceSchema>;

export const GraphBuildStatsSchema = z.object({
  people: z.number().int().nonnegative(),
  edges: z.number().int().nonnegative(),
  sources: z.number().int().nonnegative(),
  connectedComponents: z.number().int().nonnegative(),
  isolatedPeople: z.number().int().nonnegative(),
  maximumDegree: z.number().int().nonnegative(),
});

export type GraphBuildStats = z.infer<typeof GraphBuildStatsSchema>;

export const CanonicalGraphDatasetSchema = z.object({
  schemaVersion: z.literal(1),
  datasetVersion: NonEmptyStringSchema,
  generatedAt: IsoDateTimeSchema,
  snapshotDate: IsoDateSchema,
  configVersion: z.number().int().positive(),
  policyVersion: z.number().int().positive(),
  inputDigest: Sha256Schema,
  launchLenses: z.array(CoreLensIdSchema).min(1),
  sources: z.array(SourceSchema),
  people: z.array(PersonSchema),
  edges: z.array(AcceptedEdgeSchema),
  layout: GraphLayoutSchema,
  featuredTraces: z.array(FeaturedTraceSchema),
  buildStats: GraphBuildStatsSchema,
  limitations: z.array(NonEmptyStringSchema).min(1),
});

export type CanonicalGraphDataset = z.infer<typeof CanonicalGraphDatasetSchema>;

export const RuntimeGraphPersonSchema = PersonSchema.omit({
  ageStatus: true,
  selectionSignals: true,
}).extend({
  position: Point3Schema,
  searchKeys: z.array(NonEmptyStringSchema).min(1),
});

export type RuntimeGraphPerson = z.infer<typeof RuntimeGraphPersonSchema>;

export const RuntimeDiscoveryPersonSchema = PersonSchema.pick({
  id: true,
  name: true,
  description: true,
  recognitionScore: true,
  inclusionRole: true,
}).extend({
  searchKeys: z.array(NonEmptyStringSchema),
});

export type RuntimeDiscoveryPerson = z.infer<typeof RuntimeDiscoveryPersonSchema>;

export const RuntimeGraphRelationshipSchema = z
  .object({
    id: NonEmptyStringSchema,
    source: QidSchema,
    target: QidSchema,
    relation: RelationIdSchema,
    status: EdgePublicationStatusSchema,
    sourceIds: z.array(NonEmptyStringSchema).min(1),
    direction: EdgeDirectionSchema.optional(),
    start: QualifiedTimeSchema.optional(),
    end: QualifiedTimeSchema.optional(),
    lens: CoreLensIdSchema,
    label: NonEmptyStringSchema,
    note: NonEmptyStringSchema.optional(),
  })
  .superRefine((value, context) => {
    if (!relationshipTimeOrderIsValid(value.start, value.end)) {
      context.addIssue({
        code: 'custom',
        path: ['end'],
        message: 'Runtime relationship end cannot be earlier than its start.',
      });
    }
  });

export type RuntimeGraphRelationship = z.infer<typeof RuntimeGraphRelationshipSchema>;

export const RuntimeGraphIndexesSchema = z.object({
  edgeIndexesByPerson: z.array(z.array(z.number().int().nonnegative())),
  hubPersonIndexes: z.array(z.number().int().nonnegative()),
});

export type RuntimeGraphIndexes = z.infer<typeof RuntimeGraphIndexesSchema>;

export const RuntimeGraphDatasetSchema = z.object({
  schemaVersion: z.literal(2),
  datasetVersion: NonEmptyStringSchema,
  generatedAt: IsoDateTimeSchema,
  snapshotDate: IsoDateSchema,
  configVersion: z.number().int().positive(),
  policyVersion: z.number().int().positive(),
  layoutVersion: z.number().int().positive(),
  fixture: z.literal(false),
  sources: z.array(SourceSchema),
  people: z.array(RuntimeGraphPersonSchema),
  relationships: z.array(RuntimeGraphRelationshipSchema),
  indexes: RuntimeGraphIndexesSchema,
  featuredTraces: z.array(FeaturedTraceSchema),
  limitations: z.array(NonEmptyStringSchema).min(1),
});

export type RuntimeGraphDataset = z.infer<typeof RuntimeGraphDatasetSchema>;

const RuntimePartitionMetadataSchema = z.object({
  datasetVersion: NonEmptyStringSchema,
  generatedAt: IsoDateTimeSchema,
  snapshotDate: IsoDateSchema,
  configVersion: z.number().int().positive(),
  policyVersion: z.number().int().positive(),
  layoutVersion: z.number().int().positive(),
  fixture: z.literal(false),
});

export const RuntimeGraphCoreDatasetSchema = RuntimePartitionMetadataSchema.extend({
  schemaVersion: z.literal(1),
  sources: z.array(SourceSchema),
  people: z.array(RuntimeGraphPersonSchema),
  relationships: z.array(RuntimeGraphRelationshipSchema),
  featuredTraces: z.array(FeaturedTraceSchema),
  limitations: z.array(NonEmptyStringSchema).min(1),
});

export type RuntimeGraphCoreDataset = z.infer<typeof RuntimeGraphCoreDatasetSchema>;

export const RuntimeGraphContextDatasetSchema = RuntimePartitionMetadataSchema.extend({
  schemaVersion: z.literal(1),
  sources: z.array(SourceSchema),
  people: z.array(RuntimeGraphPersonSchema),
  relationships: z.array(RuntimeGraphRelationshipSchema),
  indexes: RuntimeGraphIndexesSchema,
});

export type RuntimeGraphContextDataset = z.infer<typeof RuntimeGraphContextDatasetSchema>;

export const RuntimeGraphEvidenceDatasetSchema = RuntimePartitionMetadataSchema.extend({
  schemaVersion: z.literal(1),
  sources: z.array(SourceSchema),
});

export type RuntimeGraphEvidenceDataset = z.infer<typeof RuntimeGraphEvidenceDatasetSchema>;

export function mergeRuntimeGraphPartitions(
  core: RuntimeGraphCoreDataset,
  context: RuntimeGraphContextDataset,
  evidence?: RuntimeGraphEvidenceDataset,
): RuntimeGraphDataset {
  for (const partition of [context, ...(evidence === undefined ? [] : [evidence])]) {
    for (const key of [
      'datasetVersion',
      'generatedAt',
      'snapshotDate',
      'configVersion',
      'policyVersion',
      'layoutVersion',
    ] as const) {
      if (core[key] !== partition[key]) {
        throw new Error(`Runtime graph partitions disagree on ${key}.`);
      }
    }
  }
  const sources = [...core.sources, ...context.sources, ...(evidence?.sources ?? [])].sort(
    (left, right) => left.id.localeCompare(right.id),
  );
  const people = [...core.people, ...context.people].sort((left, right) =>
    left.id.localeCompare(right.id),
  );
  const relationships = [...core.relationships, ...context.relationships].sort((left, right) =>
    left.id.localeCompare(right.id),
  );
  if (
    new Set(sources.map((source) => source.id)).size !== sources.length ||
    new Set(people.map((person) => person.id)).size !== people.length ||
    new Set(relationships.map((relationship) => relationship.id)).size !== relationships.length
  ) {
    throw new Error('Runtime graph partitions contain duplicate records.');
  }
  return RuntimeGraphDatasetSchema.parse({
    schemaVersion: 2,
    datasetVersion: core.datasetVersion,
    generatedAt: core.generatedAt,
    snapshotDate: core.snapshotDate,
    configVersion: core.configVersion,
    policyVersion: core.policyVersion,
    layoutVersion: core.layoutVersion,
    fixture: false,
    sources,
    people,
    relationships,
    indexes: context.indexes,
    featuredTraces: core.featuredTraces,
    limitations: core.limitations,
  });
}

export const RuntimeDiscoveryDatasetSchema = z
  .object({
    schemaVersion: z.union([z.literal(1), z.literal(2)]),
    datasetVersion: NonEmptyStringSchema,
    generatedAt: IsoDateTimeSchema,
    snapshotDate: IsoDateSchema,
    configVersion: z.number().int().positive(),
    policyVersion: z.number().int().positive(),
    layoutVersion: z.number().int().positive(),
    people: z.array(RuntimeDiscoveryPersonSchema),
    relationshipCounts: z.object({
      personal: z.number().int().nonnegative(),
      family: z.number().int().nonnegative(),
    }),
    featuredTraces: z.array(FeaturedTraceSchema),
    starterPersonIds: z.array(QidSchema).min(1).max(8),
  })
  .superRefine((value, context) => {
    if (value.schemaVersion === 1) {
      const emptySearchIndex = value.people.findIndex((person) => person.searchKeys.length === 0);
      if (emptySearchIndex >= 0) {
        context.addIssue({
          code: 'custom',
          path: ['people', emptySearchIndex, 'searchKeys'],
          message: 'Discovery schema version 1 requires at least one search key per person.',
        });
      }
    }
  });

export type RuntimeDiscoveryDataset = z.infer<typeof RuntimeDiscoveryDatasetSchema>;

export const RuntimeAssetDescriptorSchema = z.object({
  path: NonEmptyStringSchema,
  sha256: Sha256Schema,
  bytes: z.number().int().positive(),
  gzipBytes: z.number().int().positive(),
});

export const RuntimeGraphManifestSchema = z
  .object({
    schemaVersion: z.union([z.literal(3), z.literal(4), z.literal(5)]),
    datasetVersion: NonEmptyStringSchema,
    generatedAt: IsoDateTimeSchema,
    snapshotDate: IsoDateSchema,
    configVersion: z.number().int().positive(),
    policyVersion: z.number().int().positive(),
    layoutVersion: z.number().int().positive(),
    discovery: RuntimeAssetDescriptorSchema,
    graph: RuntimeAssetDescriptorSchema,
    context: RuntimeAssetDescriptorSchema.optional(),
    evidence: RuntimeAssetDescriptorSchema.optional(),
    counts: z.object({
      people: z.number().int().nonnegative(),
      relationships: z.number().int().nonnegative(),
      sources: z.number().int().nonnegative(),
      featuredTraces: z.number().int().nonnegative(),
      inclusionRoles: z
        .object({
          baseline: z.number().int().nonnegative(),
          connector: z.number().int().nonnegative(),
          context: z.number().int().nonnegative(),
        })
        .optional(),
    }),
  })
  .superRefine((value, context) => {
    if ((value.schemaVersion === 4 || value.schemaVersion === 5) && value.context === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['context'],
        message: `Manifest version ${value.schemaVersion} requires a context partition.`,
      });
    }
    if (value.schemaVersion === 3 && value.context !== undefined) {
      context.addIssue({
        code: 'custom',
        path: ['context'],
        message: 'Manifest version 3 cannot declare a context partition.',
      });
    }
    if (value.schemaVersion === 5 && value.evidence === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['evidence'],
        message: 'Manifest version 5 requires an evidence partition.',
      });
    }
    if (value.schemaVersion !== 5 && value.evidence !== undefined) {
      context.addIssue({
        code: 'custom',
        path: ['evidence'],
        message: `Manifest version ${value.schemaVersion} cannot declare an evidence partition.`,
      });
    }
  });

export type RuntimeGraphManifest = z.infer<typeof RuntimeGraphManifestSchema>;
